'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  AlertCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';

// Social media SVG icons (not available in installed lucide-react version)
const Instagram = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
);

const Facebook = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
  </svg>
);

const Twitter = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/>
  </svg>
);

const Linkedin = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect x="2" y="9" width="4" height="12"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

type AuthTab = 'login' | 'signup';

interface LoginForm {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupForm {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

export default function AuthClient() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const [tab, setTab] = useState<AuthTab>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [forgotError, setForgotError] = useState('');

  const loginForm = useForm<LoginForm>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const signupForm = useForm<SignupForm>({
    defaultValues: { name: '', email: '', password: '', confirmPassword: '', agreeTerms: false },
  });

  async function handleLogin(data: LoginForm) {
    setAuthError('');
    setIsSubmitting(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back!');
      router.push('/dashboard');
      router.refresh();
    } catch (error: any) {
      setAuthError(error?.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSignup(data: SignupForm) {
    setAuthError('');
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await signUp(data.email, data.password, { fullName: data.name });
      // If session is returned immediately (email confirmation disabled), redirect to dashboard
      if (result?.session) {
        toast.success('Account created! Welcome to Infinite Content.');
        router.push('/dashboard');
        router.refresh();
      } else {
        // Email confirmation required — show message instead of redirecting
        toast.success('Account created! Check your email to confirm, then sign in.');
        setTab('login');
      }
    } catch (error: any) {
      setAuthError(error?.message || 'Failed to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!forgotEmail) {
      setForgotError('Please enter your email address.');
      return;
    }
    setForgotStatus('sending');
    setForgotError('');
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback?type=recovery`,
      });
      if (error) throw error;
      setForgotStatus('sent');
    } catch (err: any) {
      setForgotError(err?.message || 'Failed to send reset email. Please try again.');
      setForgotStatus('error');
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative bg-gradient-to-br from-[#6C47FF] via-[#7B57FF] to-[#9B7BFF] flex-col items-start justify-between p-12 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute top-1/3 -left-16 w-64 h-64 rounded-full bg-white/5" />
          <div className="absolute bottom-20 right-10 w-48 h-48 rounded-full bg-white/5" />
          <div className="absolute bottom-0 left-0 w-full h-64 bg-gradient-to-t from-black/10 to-transparent" />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <AppLogo size={40} />
          <span className="text-white font-bold text-[20px] tracking-tight">Infinite Content</span>
        </div>

        {/* Hero copy */}
        <div className="relative">
          <h1 className="text-[38px] font-bold text-white leading-tight tracking-tight max-w-md">
            Schedule smarter.
            <br />
            Publish everywhere.
          </h1>
          <p className="text-white/70 text-[16px] mt-4 max-w-sm leading-relaxed">
            Manage all your social media channels from one dashboard. Schedule posts, track performance, and grow your audience.
          </p>

          {/* Platform logos row */}
          <div className="flex items-center gap-3 mt-8">
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Instagram size={20} className="text-white" />
            </div>
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Facebook size={20} className="text-white" />
            </div>
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Twitter size={20} className="text-white" />
            </div>
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Linkedin size={20} className="text-white" />
            </div>
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.73a4.85 4.85 0 0 1-1.01-.04z" />
              </svg>
            </div>
            <span className="text-white/50 text-[13px] ml-1">& more</span>
          </div>

          {/* Social proof */}
          <div className="flex items-center gap-4 mt-8">
            <div className="flex -space-x-2">
              {['#6C47FF', '#FF6B35', '#10b981', '#f59e0b'].map((color, i) => (
                <div
                  key={`avatar-brand-${i}`}
                  className="w-8 h-8 rounded-full border-2 border-white/30 flex items-center justify-center text-white text-[10px] font-bold"
                  style={{ backgroundColor: color }}
                >
                  {['JK', 'AM', 'TR', 'LP'][i]}
                </div>
              ))}
            </div>
            <p className="text-white/70 text-[13px]">
              Trusted by <strong className="text-white">12,000+</strong> marketers
            </p>
          </div>
        </div>

        {/* Bottom quote */}
        <div className="relative bg-white/10 backdrop-blur rounded-xl p-4 max-w-sm">
          <p className="text-white/90 text-[13px] leading-relaxed italic">
            &ldquo;Infinite Content cut our scheduling time by 60%. The recurring post feature alone saves us 5 hours a week.&rdquo;
          </p>
          <p className="text-white/60 text-[11px] mt-2 font-medium">
            — Aisha Mensah, Head of Social @ TechFlow
          </p>
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-8 md:py-12 bg-[rgb(248,248,252)] overflow-y-auto">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <AppLogo size={36} />
          <span className="font-bold text-[18px] text-[rgb(15,15,25)] tracking-tight">Infinite Content</span>
        </div>

        <div className="w-full max-w-[400px]">
          {/* FORGOT PASSWORD VIEW */}
          {showForgotPassword ? (
            <div>
              <button
                type="button"
                onClick={() => { setShowForgotPassword(false); setForgotStatus('idle'); setForgotError(''); setForgotEmail(''); }}
                className="flex items-center gap-1.5 text-[13px] text-[rgb(120,120,145)] hover:text-[rgb(60,60,80)] mb-6 transition-colors"
              >
                <ArrowLeft size={14} />
                Back to sign in
              </button>

              {forgotStatus === 'sent' ? (
                <div className="text-center py-4">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <Mail size={24} className="text-green-600" />
                  </div>
                  <h2 className="text-[22px] font-bold text-[rgb(15,15,25)] tracking-tight mb-2">Check your email</h2>
                  <p className="text-[14px] text-[rgb(120,120,145)] leading-relaxed">
                    We sent a password reset link to <strong className="text-[rgb(60,60,80)]">{forgotEmail}</strong>. Check your inbox and follow the instructions.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(false); setForgotStatus('idle'); setForgotEmail(''); }}
                    className="mt-6 w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-[14px] font-semibold rounded-xl transition-all"
                  >
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <h2 className="text-[24px] font-bold text-[rgb(15,15,25)] tracking-tight">Reset your password</h2>
                    <p className="text-[14px] text-[rgb(120,120,145)] mt-1">
                      Enter your email and we&apos;ll send you a reset link.
                    </p>
                  </div>

                  {forgotError && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl mb-4">
                      <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-[12.5px] text-red-600">{forgotError}</p>
                    </div>
                  )}

                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className="block text-[12.5px] font-semibold text-[rgb(60,60,80)] mb-1.5">
                        Email address
                      </label>
                      <div className="relative">
                        <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(120,120,145)]" />
                        <input
                          type="email"
                          placeholder="you@company.com"
                          value={forgotEmail}
                          onChange={e => setForgotEmail(e.target.value)}
                          className="w-full pl-9 pr-4 py-2.5 text-[13.5px] bg-white border border-[rgb(226,226,238)] rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all placeholder:text-[rgb(180,180,200)]"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={forgotStatus === 'sending'}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white text-[14px] font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] shadow-sm"
                    >
                      {forgotStatus === 'sending' ? (
                        <><Loader2 size={15} className="animate-spin" /> Sending...</>
                      ) : (
                        <><Mail size={15} /> Send Reset Link</>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-[24px] font-bold text-[rgb(15,15,25)] tracking-tight">
                  {tab === 'login' ? 'Welcome back' : 'Create your account'}
                </h2>
                <p className="text-[14px] text-[rgb(120,120,145)] mt-1">
                  {tab === 'login' ? 'Sign in to manage your social media content.' : 'Start scheduling smarter in minutes.'}
                </p>
              </div>

              {/* Tab switcher */}
              <div className="flex bg-[rgb(226,226,238)] rounded-xl p-1 mb-6">
                {[
                  { id: 'tab-login', value: 'login' as AuthTab, label: 'Sign In' },
                  { id: 'tab-signup', value: 'signup' as AuthTab, label: 'Create Account' },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setTab(t.value); setAuthError(''); }}
                    className={`flex-1 py-2 rounded-lg text-[13.5px] font-semibold transition-all duration-200 ${
                      tab === t.value
                        ? 'bg-white text-[rgb(15,15,25)] shadow-card'
                        : 'text-[rgb(120,120,145)] hover:text-[rgb(80,80,100)]'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Google OAuth */}
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 bg-white border border-[rgb(226,226,238)] rounded-xl text-[14px] font-medium text-[rgb(30,30,45)] hover:bg-[rgb(244,244,250)] hover:border-[rgb(200,200,220)] transition-all shadow-card mb-4"
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-[rgb(226,226,238)]" />
                <span className="text-[12px] text-[rgb(120,120,145)] font-medium">or</span>
                <div className="flex-1 h-px bg-[rgb(226,226,238)]" />
              </div>

              {/* Auth error */}
              {authError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl mb-4 animate-fade-in">
                  <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[12.5px] text-red-600">{authError}</p>
                </div>
              )}

              {/* LOGIN FORM */}
              {tab === 'login' && (
                <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                  <div>
                    <label className="block text-[12.5px] font-semibold text-[rgb(60,60,80)] mb-1.5">
                      Email address
                    </label>
                    <div className="relative">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(120,120,145)]" />
                      <input
                        type="email"
                        placeholder="sofia@brandhq.io"
                        {...loginForm.register('email', {
                          required: 'Email is required',
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                        })}
                        className={`w-full pl-9 pr-4 py-2.5 text-[13.5px] bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all placeholder:text-[rgb(180,180,200)] ${
                          loginForm.formState.errors.email ? 'border-red-300' : 'border-[rgb(226,226,238)]'
                        }`}
                      />
                    </div>
                    {loginForm.formState.errors.email && (
                      <p className="text-[11.5px] text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle size={11} />
                        {loginForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-[12.5px] font-semibold text-[rgb(60,60,80)]">
                        Password
                      </label>
                      <button
                        type="button"
                        onClick={() => { setShowForgotPassword(true); setForgotEmail(loginForm.getValues('email') || ''); setForgotStatus('idle'); setForgotError(''); }}
                        className="text-[12px] text-primary-500 hover:text-primary-600 font-medium transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(120,120,145)]" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        {...loginForm.register('password', { required: 'Password is required' })}
                        className={`w-full pl-9 pr-10 py-2.5 text-[13.5px] bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all placeholder:text-[rgb(180,180,200)] ${
                          loginForm.formState.errors.password ? 'border-red-300' : 'border-[rgb(226,226,238)]'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(120,120,145)] hover:text-[rgb(80,80,100)] transition-colors"
                      >
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="text-[11.5px] text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle size={11} />
                        {loginForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="rememberMe"
                      {...loginForm.register('rememberMe')}
                      className="w-4 h-4 rounded border-[rgb(226,226,238)] text-primary-500 focus:ring-primary-300"
                    />
                    <label htmlFor="rememberMe" className="text-[13px] text-[rgb(80,80,100)] cursor-pointer">
                      Remember me for 30 days
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white text-[14px] font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] shadow-sm"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        Sign In
                        <ArrowRight size={15} />
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* SIGNUP FORM */}
              {tab === 'signup' && (
                <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
                  <div>
                    <label className="block text-[12.5px] font-semibold text-[rgb(60,60,80)] mb-1.5">
                      Full name
                    </label>
                    <div className="relative">
                      <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(120,120,145)]" />
                      <input
                        type="text"
                        placeholder="Sofia Martinez"
                        {...signupForm.register('name', { required: 'Full name is required' })}
                        className={`w-full pl-9 pr-4 py-2.5 text-[13.5px] bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all placeholder:text-[rgb(180,180,200)] ${
                          signupForm.formState.errors.name ? 'border-red-300' : 'border-[rgb(226,226,238)]'
                        }`}
                      />
                    </div>
                    {signupForm.formState.errors.name && (
                      <p className="text-[11.5px] text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle size={11} />
                        {signupForm.formState.errors.name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[12.5px] font-semibold text-[rgb(60,60,80)] mb-1.5">
                      Work email
                    </label>
                    <div className="relative">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(120,120,145)]" />
                      <input
                        type="email"
                        placeholder="you@company.com"
                        {...signupForm.register('email', {
                          required: 'Email is required',
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                        })}
                        className={`w-full pl-9 pr-4 py-2.5 text-[13.5px] bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all placeholder:text-[rgb(180,180,200)] ${
                          signupForm.formState.errors.email ? 'border-red-300' : 'border-[rgb(226,226,238)]'
                        }`}
                      />
                    </div>
                    {signupForm.formState.errors.email && (
                      <p className="text-[11.5px] text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle size={11} />
                        {signupForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[12.5px] font-semibold text-[rgb(60,60,80)] mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(120,120,145)]" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Min. 8 characters"
                        {...signupForm.register('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' },
                        })}
                        className={`w-full pl-9 pr-10 py-2.5 text-[13.5px] bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all placeholder:text-[rgb(180,180,200)] ${
                          signupForm.formState.errors.password ? 'border-red-300' : 'border-[rgb(226,226,238)]'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(120,120,145)] hover:text-[rgb(80,80,100)] transition-colors"
                      >
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {signupForm.formState.errors.password && (
                      <p className="text-[11.5px] text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle size={11} />
                        {signupForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[12.5px] font-semibold text-[rgb(60,60,80)] mb-1.5">
                      Confirm password
                    </label>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(120,120,145)]" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Repeat your password"
                        {...signupForm.register('confirmPassword', {
                          required: 'Please confirm your password',
                        })}
                        className={`w-full pl-9 pr-10 py-2.5 text-[13.5px] bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all placeholder:text-[rgb(180,180,200)] ${
                          signupForm.formState.errors.confirmPassword ? 'border-red-300' : 'border-[rgb(226,226,238)]'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((p) => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(120,120,145)] hover:text-[rgb(80,80,100)] transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {signupForm.formState.errors.confirmPassword && (
                      <p className="text-[11.5px] text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle size={11} />
                        {signupForm.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      {...signupForm.register('agreeTerms', { required: 'You must agree to the terms' })}
                      className="w-4 h-4 mt-0.5 rounded border-[rgb(226,226,238)] text-primary-500 focus:ring-primary-300 flex-shrink-0"
                    />
                    <label htmlFor="agreeTerms" className="text-[12.5px] text-[rgb(80,80,100)] cursor-pointer leading-snug">
                      I agree to the{' '}
                      <span className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
                        Terms of Service
                      </span>{' '}
                      and{' '}
                      <span className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
                        Privacy Policy
                      </span>
                    </label>
                  </div>
                  {signupForm.formState.errors.agreeTerms && (
                    <p className="text-[11.5px] text-red-500 flex items-center gap-1">
                      <AlertCircle size={11} />
                      {signupForm.formState.errors.agreeTerms.message}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white text-[14px] font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] shadow-sm"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      <>
                        Create Account
                        <ArrowRight size={15} />
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* Bottom link */}
              <p className="text-center text-[12.5px] text-[rgb(120,120,145)] mt-5">
                {tab === 'login' ? (
                  <>
                    Don&apos;t have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setTab('signup')}
                      className="text-primary-500 hover:text-primary-600 font-semibold transition-colors"
                    >
                      Sign up free
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setTab('login')}
                      className="text-primary-500 hover:text-primary-600 font-semibold transition-colors"
                    >
                      Sign in
                    </button>
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}